import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Microscope, Award } from "lucide-react";
import { HERO_IMAGE, SCIENCE_ABSTRACT, LAB_WORKER, PRODUCTS, CATEGORIES } from "../data/products";
import { ProductCard } from "../components/ProductCard";
import { usePageMeta } from "../hooks/usePageMeta";

const features = [
    { icon: ShieldCheck, title: "Premium Research-Grade", text: "Carefully sourced compounds, presented for laboratory work." },
    { icon: FlaskConical, title: "High-Purity Formulations", text: "Lyophilized peptides prepared for sensitive research." },
    { icon: Microscope, title: "Clean Labeling", text: "Every vial clearly identifies compound, dosage and use." },
    { icon: Award, title: "Organized Catalog", text: "Curated library organized by category for focused selection." },
];

export const Home = () => {
    usePageMeta({
        title: "Quantum Genesis Labs — Premium Research Peptides & Research Compounds",
        description: "Premium research-grade peptides — BPC-157, TB-500, Retatrutide, Tirzepatide, GHK-Cu, MOTS-C, NAD+ and more. High-purity lyophilized formulations for laboratory research use only.",
        path: "/",
    });
    // Manually curated flagship lineup — easy to edit by reordering this list.
    const FEATURED_IDS = [
        "retatrutide",
        "tirzepatide",
        "bpc-157",
        "tb-500",
        "mots-c",
        "ghk-cu",
        "glow-blend",
    ];
    const featured = FEATURED_IDS
        .map((id) => PRODUCTS.find((p) => p.id === id))
        .filter(Boolean);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/5 min-h-[78vh] sm:min-h-[88vh] flex items-center">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt=""
                        className="w-full h-full object-cover opacity-55 ambient-drift"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/45 via-[#05050A]/72 to-[#05050A]" />
                </div>

                {/* Soft ambient glow orbs */}
                <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] sm:w-[520px] h-[420px] sm:h-[520px] rounded-full bg-indigo-600/20 blur-[120px] glow-orb" />
                <div className="pointer-events-none absolute -bottom-40 right-0 w-[360px] sm:w-[460px] h-[360px] sm:h-[460px] rounded-full bg-violet-600/15 blur-[140px] ambient-pulse" />

                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 sm:py-28 lg:py-44 w-full">
                    <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-indigo-400/90 mb-5 sm:mb-7 fade-up">
                        Premium Research Peptides
                    </p>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.04em] max-w-3xl leading-[1.02] fade-up fade-up-delay-1">
                        Precision biology, <br />
                        <span className="text-gradient">delivered in glass.</span>
                    </h1>
                    <p className="mt-5 sm:mt-8 text-base md:text-lg text-slate-300/85 max-w-xl leading-relaxed fade-up fade-up-delay-2">
                        A curated catalog of premium research-grade peptides — high-purity lyophilized formulations,
                        carefully sourced and professionally presented.
                    </p>
                    <div className="mt-8 sm:mt-12 flex flex-wrap items-center gap-3 fade-up fade-up-delay-3">
                        <Link
                            to="/products"
                            data-testid="hero-shop-catalog"
                            className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white px-6 sm:px-7 py-3 sm:py-3.5 text-sm font-medium btn-glow"
                        >
                            Browse Catalog <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-contact-link"
                            className="btn-ghost inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-indigo-400/50 px-6 sm:px-7 py-3 sm:py-3.5 text-sm text-slate-200"
                        >
                            Research Inquiries
                        </Link>
                    </div>

                    {/* Trust pill */}
                    <div className="mt-10 sm:mt-14 fade-up fade-up-delay-4">
                        <span
                            data-testid="hero-trust-pill"
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-indigo-500/25 bg-indigo-500/5 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-indigo-200/90 trust-pulse"
                        >
                            <span className="w-1 h-1 rounded-full bg-indigo-400 shadow-[0_0_10px_rgba(129,140,248,0.7)]" />
                            Premium Research-Grade Catalog
                        </span>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 sm:py-20 lg:py-28" data-testid="features-section">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {features.map((f, i) => (
                        <div
                            key={i}
                            className="reveal rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-7 card-hover"
                            style={{ transitionDelay: `${i * 60}ms` }}
                            data-testid={`feature-card-${i}`}
                        >
                            <span className="inline-flex w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/20 items-center justify-center mb-5">
                                <f.icon className="w-5 h-5 text-indigo-400" />
                            </span>
                            <h3 className="text-lg font-medium text-white mb-2">{f.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CATEGORY STRIP */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 reveal">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-3">Categories</p>
                        <h2 className="text-3xl md:text-4xl font-light tracking-[-0.02em]">Research-grade by category</h2>
                    </div>
                    <Link to="/products" className="text-sm text-slate-400 hover:text-indigo-300 hidden sm:inline-flex items-center gap-1 transition-colors">
                        View all <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="flex flex-wrap gap-2.5">
                    {CATEGORIES.slice(1).map((c) => (
                        <Link
                            key={c}
                            to={`/products?category=${encodeURIComponent(c)}`}
                            data-testid={`home-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="btn-ghost px-4 py-2 rounded-full text-sm bg-white/[0.03] border border-white/10 text-slate-300 hover:border-indigo-400/40 hover:text-white"
                        >
                            {c}
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 sm:py-20 reveal">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-3">Featured</p>
                        <h2 className="text-3xl md:text-4xl font-light tracking-[-0.02em]">This month's catalog</h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="featured-products-grid">
                    {featured.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
                <div className="mt-12 flex justify-center">
                    <Link
                        to="/products"
                        data-testid="view-all-products-button"
                        className="btn-ghost inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-indigo-400/50 px-7 py-3.5 text-sm text-slate-200"
                    >
                        View full catalog <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* TRUST / SCIENCE BANNER */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 items-center reveal">
                <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
                    <img src={LAB_WORKER} alt="Laboratory researcher" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#05050A] via-transparent to-transparent" />
                </div>
                <div>
                    <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-5">Our Standard</p>
                    <h2 className="text-3xl md:text-4xl font-light tracking-[-0.02em] leading-tight">
                        Premium compounds, <span className="text-gradient">cleanly presented.</span>
                    </h2>
                    <p className="mt-6 text-slate-300/85 leading-relaxed">
                        Quantum Genesis Labs curates research-grade peptides with a focus on clarity and presentation.
                        Every compound is a high-purity lyophilized powder, clearly labeled with its name, dosage and
                        research-use disclosure.
                    </p>
                    <ul className="mt-8 space-y-3 text-sm text-slate-300">
                        <li className="flex gap-2.5"><span className="text-indigo-400">●</span> Premium research-grade compounds</li>
                        <li className="flex gap-2.5"><span className="text-indigo-400">●</span> Carefully sourced and professionally presented</li>
                        <li className="flex gap-2.5"><span className="text-indigo-400">●</span> High-purity lyophilized formulations</li>
                        <li className="flex gap-2.5"><span className="text-indigo-400">●</span> Clean labeling and organized catalog</li>
                        <li className="flex gap-2.5"><span className="text-indigo-400">●</span> Research-use products only</li>
                    </ul>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-6xl mx-auto px-5 lg:px-10 pb-20 sm:pb-28 reveal">
                <div className="relative rounded-3xl border border-white/10 overflow-hidden p-12 md:p-20 text-center">
                    <div className="absolute inset-0 -z-10">
                        <img src={SCIENCE_ABSTRACT} alt="" className="w-full h-full object-cover opacity-30 ambient-drift" />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12]/72 to-[#05050A]/92" />
                    </div>
                    <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full bg-indigo-500/15 blur-[110px] ambient-pulse" />
                    <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-5">For Research Purposes Only</p>
                    <h2 className="text-3xl md:text-5xl font-light tracking-[-0.03em] max-w-2xl mx-auto leading-tight">
                        Equip your lab with peptides you can trust.
                    </h2>
                    <Link
                        to="/products"
                        data-testid="cta-explore-catalog"
                        className="mt-10 inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 px-8 py-3.5 text-sm font-medium btn-glow"
                    >
                        Explore the catalog <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
